import type { Vertical } from '@cvsi/shared';

export interface Terminology {
  profileName: string;
  movement: string;
  movementPlural: string;
  movementRef: string;
  subject: string;
  provider: string;
  asset: string;
  cost: string;
  extraAmount: string;
}

const DEFAULT_TERMINOLOGY: Terminology = {
  profileName: 'Generic movement',
  movement: 'movement',
  movementPlural: 'movements',
  movementRef: 'Movement reference',
  subject: 'subject',
  provider: 'operator',
  asset: 'asset',
  cost: 'cost',
  extraAmount: 'additional amount',
};

const VERTICAL_TERMINOLOGY: Record<string, Partial<Terminology>> = {
  generic: DEFAULT_TERMINOLOGY,
  other: DEFAULT_TERMINOLOGY,
  rideshare: {
    profileName: 'Rideshare',
    movement: 'ride',
    movementPlural: 'rides',
    movementRef: 'Ride reference',
    subject: 'rider',
    provider: 'driver',
    asset: 'vehicle',
    cost: 'fare',
    extraAmount: 'tip',
  },
  nemt: {
    profileName: 'NEMT',
    movement: 'transport',
    movementPlural: 'transports',
    movementRef: 'Transport reference',
    subject: 'passenger',
    provider: 'provider',
    asset: 'vehicle',
  },
  municipal_transit: {
    profileName: 'Transit',
    movement: 'trip',
    movementPlural: 'trips',
    movementRef: 'Trip reference',
    subject: 'passenger',
    provider: 'operator',
    asset: 'vehicle',
  },
  school_bus: {
    profileName: 'School transportation',
    movement: 'route event',
    movementPlural: 'route events',
    movementRef: 'Route reference',
    subject: 'student',
    provider: 'driver',
    asset: 'bus',
  },
  rail: {
    profileName: 'Rail',
    movement: 'journey',
    movementPlural: 'journeys',
    movementRef: 'Journey reference',
    subject: 'passenger',
    provider: 'operator',
    asset: 'train',
  },
  food_delivery: {
    profileName: 'Delivery',
    movement: 'delivery',
    movementPlural: 'deliveries',
    movementRef: 'Delivery reference',
    subject: 'recipient',
    provider: 'courier',
    asset: 'order',
  },
  logistics: {
    profileName: 'Logistics',
    movement: 'shipment',
    movementPlural: 'shipments',
    movementRef: 'Shipment reference',
    subject: 'custody item',
    provider: 'handler',
    asset: 'asset',
  },
  freight: {
    profileName: 'Freight',
    movement: 'shipment',
    movementPlural: 'shipments',
    movementRef: 'Shipment reference',
    subject: 'custody item',
    provider: 'carrier',
    asset: 'trailer',
  },
  automotive_logistics: {
    profileName: 'Automotive logistics',
    movement: 'vehicle transfer',
    movementPlural: 'vehicle transfers',
    movementRef: 'Vehicle transfer reference',
    subject: 'vehicle',
    provider: 'carrier',
    asset: 'vehicle',
  },
  'automotive-logistics': {
    profileName: 'Automotive logistics',
    movement: 'vehicle transfer',
    movementPlural: 'vehicle transfers',
    movementRef: 'Vehicle transfer reference',
    subject: 'vehicle',
    provider: 'carrier',
    asset: 'vehicle',
  },
  high_value_assets: {
    profileName: 'High-value assets',
    movement: 'asset movement',
    movementPlural: 'asset movements',
    movementRef: 'Asset reference',
    subject: 'custody item',
    provider: 'handler',
    asset: 'asset',
  },
};

export function terminologyForVertical(vertical: Vertical | string | null | undefined): Terminology {
  const key = vertical ? String(vertical).trim().toLowerCase() : '';
  const override = key ? VERTICAL_TERMINOLOGY[key] : undefined;
  return { ...DEFAULT_TERMINOLOGY, ...override };
}

export function labelize(value: string | null | undefined): string {
  if (!value) return '-';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface EventTypePresentation {
  label: string;
  shortLabel: string;
  description: string;
  tone: 'blue' | 'green' | 'amber' | 'purple' | 'red' | 'slate';
}

const EVENT_TYPE_PRESENTATION: Record<string, EventTypePresentation> = {
  pickup: { label: 'Pickup verification', shortLabel: 'Pickup', description: 'Confirms the person or load was collected as assigned.', tone: 'blue' },
  dropoff: { label: 'Drop-off verification', shortLabel: 'Drop-off', description: 'Confirms the movement reached its recorded destination.', tone: 'green' },
  boarding: { label: 'Boarding verification', shortLabel: 'Boarding', description: 'Confirms the subject boarded the assigned vehicle or service.', tone: 'blue' },
  vehicle_scan: { label: 'Vehicle scan', shortLabel: 'Vehicle scan', description: 'Confirms the vehicle token and assignment.', tone: 'purple' },
  operator_verify: { label: 'Operator verification', shortLabel: 'Operator', description: 'Confirms the operator identity or credential.', tone: 'purple' },
  handoff: { label: 'Handoff verification', shortLabel: 'Handoff', description: 'Confirms a person, order, or asset changed hands.', tone: 'amber' },
  load_transfer: { label: 'Load transfer', shortLabel: 'Load transfer', description: 'Records movement of a load between assigned parties.', tone: 'amber' },
  custody_handoff: { label: 'Custody handoff', shortLabel: 'Custody', description: 'Confirms a documented chain-of-custody transfer.', tone: 'amber' },
  arrival_confirmation: { label: 'Arrival confirmation', shortLabel: 'Arrival', description: 'Records the subject’s later confirmation of safe arrival.', tone: 'green' },
  exception: { label: 'Exception', shortLabel: 'Exception', description: 'Records an event that needs attention or explanation.', tone: 'red' },
};

export function eventTypePresentation(value: string | null | undefined): EventTypePresentation {
  const key = String(value ?? '').trim().toLowerCase();
  return EVENT_TYPE_PRESENTATION[key] ?? {
    label: labelize(value),
    shortLabel: labelize(value),
    description: 'Records a verification or movement event received by CVSI.',
    tone: 'slate',
  };
}

export function eventTypeLabel(value: string | null | undefined): string {
  return eventTypePresentation(value).label;
}

export function eventTypeShortLabel(value: string | null | undefined): string {
  return eventTypePresentation(value).shortLabel;
}

const REASON_LABELS: Record<string, string> = {
  QR_VALID: 'Vehicle QR token was valid', VEHICLE_ACTIVE: 'Vehicle was active', ASSIGNMENT_MATCH: 'Assignment matched', LOCATION_WINDOW_OK: 'Location was within the expected area',
  SIGNATURE_INVALID: 'Vehicle QR signature was invalid', TENANT_MISMATCH: 'Vehicle token belonged to another tenant', TOKEN_EXPIRED: 'Vehicle token had expired', TOKEN_REVOKED_OR_UNKNOWN: 'Vehicle token was revoked or not recognized', VEHICLE_NOT_ACTIVE: 'Vehicle was not active', VEHICLE_TOKEN_REVOKED: 'Vehicle token was revoked',
  MISSING_LOCATION: 'Location was not provided', LOCATION_DEVIATION: 'Location differed from the expected area',
  LOW_LOCATION_CONFIDENCE: 'Location was not precise enough', LOCATION_WINDOW_FAILED: 'Location was outside the expected area',
  VERIFICATION_NOT_CLEAN: 'Verification was not confirmed', ASSIGNMENT_MISMATCH: 'Assignment details did not match',
  VEHICLE_OPERATOR_ASSIGNMENT_MISMATCH: 'Operator and vehicle assignment did not match', OUTSIDE_TIME_WINDOW: 'The event occurred outside its expected time window',
  DEVICE_TRUST_LOW: 'The device trust signal was incomplete', CUSTODY_CHAIN_GAP: 'The custody handoff did not match the prior custodian', FREIGHT_TEMP_EXCEPTION: 'The recorded freight temperature exceeded the allowed threshold', REPEAT_ZONE_FAILURE: 'This area has repeated verification issues',
};

export function reasonLabel(value: string | null | undefined): string { return value ? REASON_LABELS[value.toUpperCase()] ?? labelize(value) : '-'; }
export function riskLabel(value: string | null | undefined): string {
  if (value === 'low') return 'Verified';
  if (value === 'monitor') return 'Monitor';
  if (value === 'elevated' || value === 'review_required' || value === 'manual_review') return 'Review required';
  if (value === 'failed' || value === 'mismatch' || value === 'expired' || value === 'revoked' || value === 'not_assigned') return 'Failed';
  return value ? labelize(value) : 'Unknown';
}
export function recordStatus(event: { riskBucket: string; review?: unknown; customerReview?: unknown }): string {
  return event.review || event.customerReview ? 'reviewed' : event.riskBucket;
}
export function requiresHumanReview(event: { humanReviewRequired?: boolean; review?: unknown; customerReview?: unknown }): boolean {
  return Boolean(event.humanReviewRequired && !event.review && !event.customerReview);
}
export function reviewStatusLabel(event: { humanReviewRequired?: boolean; review?: unknown; customerReview?: unknown }): string {
  if (event.review || event.customerReview) return 'Reviewed';
  return event.humanReviewRequired ? 'Review required' : 'Not reviewed';
}
export function reviewStatusValue(event: { humanReviewRequired?: boolean; review?: unknown; customerReview?: unknown }): 'reviewed' | 'review_required' | 'not_reviewed' {
  if (event.review || event.customerReview) return 'reviewed';
  return event.humanReviewRequired ? 'review_required' : 'not_reviewed';
}
export function reviewedDocumentStatus(event: { review?: unknown; customerReview?: unknown }, fallback: string): string {
  return event.review || event.customerReview ? 'reviewed' : fallback;
}
export function verificationLabel(value: string | null | undefined): string {
  if (value === 'verified') return 'Verified';
  if (value === 'review_required' || value === 'manual_review') return 'Needs review';
  if (value === 'mismatch' || value === 'expired' || value === 'revoked' || value === 'not_assigned') return 'Failed';
  return value ? labelize(value) : 'Needs review';
}

export function sourceModeLabel(value: string | null | undefined): string {
  if (value === 'live') return 'Live API record';
  if (value === 'test') return 'Test API record';
  return 'Source mode not recorded';
}
